import { cn } from '@/lib/utils';
import { ElementType, HTMLAttributes } from 'react';

/*
 * The public site's type system.
 *
 * Display* is Fraunces (see fontFamily.display in tailwind.config.js): a warm,
 * slightly irregular serif that carries the craft side of the academy. It sets
 * page and section headings only — never nav links, never card labels.
 * Everything else is Manrope.
 *
 * Every tier steps at `md:` as well as `lg:`. The previous scale jumped only at
 * `lg:`, which left 768–1023px rendering mobile type at desktop width.
 */

type TextProps = HTMLAttributes<HTMLElement> & {
  children: React.ReactNode;
  /** Override the rendered tag without touching the visual style — a card
   *  title using the `Large` style, say, may need to be an `h3` or `h4`
   *  depending on what heading level precedes it on a given page. */
  as?: ElementType;
};

const text = (Tag: ElementType, base: string, displayName: string) => {
  const Component = ({ as, className, children, ...props }: TextProps) => {
    const Rendered = as ?? Tag;

    return (
      <Rendered className={cn(base, className)} {...props}>
        {children}
      </Rendered>
    );
  };
  Component.displayName = displayName;
  return Component;
};

const Display1 = text(
  'h1',
  'font-display font-semibold scroll-m-20 tracking-tight text-balance text-4xl md:text-5xl lg:text-6xl',
  'Display1'
);

const Display2 = text(
  'h2',
  'font-display font-semibold scroll-m-20 tracking-tight text-balance text-3xl md:text-4xl lg:text-5xl',
  'Display2'
);

const Display3 = text(
  'h3',
  'font-display font-semibold scroll-m-20 tracking-tight text-balance text-2xl md:text-3xl lg:text-4xl',
  'Display3'
);

const H1 = text(
  'h1',
  'scroll-m-20 font-bold tracking-tight text-balance text-2xl md:text-3xl lg:text-4xl',
  'H1'
);

const H2 = text(
  'h2',
  'scroll-m-20 font-bold tracking-tight text-balance text-xl md:text-2xl lg:text-3xl',
  'H2'
);

const H3 = text(
  'h3',
  'scroll-m-20 font-bold tracking-tight text-balance text-lg md:text-xl lg:text-2xl',
  'H3'
);

const Large = text(
  'h4',
  'scroll-m-20 font-semibold text-balance text-base md:text-lg lg:text-xl',
  'Large'
);

/**
 * Small-caps label above a heading. Names the section without competing with
 * it, and is the only place letter-spacing is opened up.
 */
const Eyebrow = text(
  'p',
  'font-semibold uppercase tracking-[0.18em] text-primary text-xs md:text-sm',
  'Eyebrow'
);

const Body = text(
  'p',
  'leading-relaxed text-pretty text-base md:text-[1.0625rem] lg:text-lg',
  'Body'
);

const Subtle = text(
  'p',
  'leading-relaxed text-pretty text-muted-foreground text-sm md:text-[0.9375rem] lg:text-base',
  'Subtle'
);

const Small = text(
  'p',
  'leading-relaxed text-muted-foreground text-xs md:text-sm',
  'Small'
);

export default {
  Display1,
  Display2,
  Display3,
  H1,
  H2,
  H3,
  Large,
  Eyebrow,
  Body,
  Subtle,
  Small,
};
